import type { ClassDefinition } from '../../schemas/classDefinition';
import { ladroAdvanced2to5Moves } from '../moves/ladro/advanced-2-5';
import { ladroAdvanced6to10Moves } from '../moves/ladro/advanced-6-10';
import { ladroStartingAutoMoves, ladroStartingChoiceMoves } from '../moves/ladro/initial';

/**
 * Definizione della classe **Ladro** (manuale V1, rr. 3668-3998).
 *
 * Prima classe pilota del data-entry DWR. Le decisioni prese qui (struttura
 * file, naming ID, gestione casi peculiari) servono da template per le
 * altre 11 classi.
 *
 * **Punti notevoli**:
 * - Dado di danno **d8**, PF base 6, Carico base 9
 * - 5 archetipi razza-tematici (uno per Umano/Elfo/Nano/Halfling/Mezz'orco)
 * - 4 mosse iniziali automatiche + 3 mosse iniziali di scelta (`pickCount: 1`)
 * - 16 mosse avanzate L2-5, 14 mosse avanzate L6-10
 * - 4 template di Legami (= `maxBonds`)
 * - **Non casteggiante**: `spellcasting` resta `undefined`
 * - **10 monete iniziali**: modellate come Item con `quantity: 10` (FI-16 risolto)
 */
export const ladroClassDefinition: ClassDefinition = {
  id: 'ladro',
  name: 'Ladro',

  intro:
    "Li osservi, seduti in cerchio attorno al fuoco, a raccontare storie di battaglie e di favori divini, illudendosi di essere eroi. Bella favola! Tu, con le monete che ti bruciano in tasca, butti lì un mezzo sorriso. Conosci la *verità*: questo Dungeon World è nient'altro che una grossa, lurida, maledetta occasione per fare cassa!\n\nE come fare lo sai benissimo, in ogni dannato modo possibile. Furti, certo. Ma anche veleni che non lasciano traccia, trappole ingegnose e mortali, o magari un arsenale di trucchetti che ti toglie dai guai. Oppure, preferisci i guanti di velluto? Quando i tuoi contatti si muovono per te, la parlantina incanta o confonde, e la mano da artista trasforma un pezzo di carta in una fortuna. Sai come muoverti, come tessere la tua tela sociale per ottenere favori... e a volte, diciamocelo, come far sì che le cose vadano proprio come vuoi tu, un colpo di fortuna che non è solo fortuna.\n\nTi danno del codardo ogni volta che preferisci sparire invece di fare l'eroe, ma senza di te si sarebbero già trovati con un dardo avvelenato nella schiena o bloccati da una serratura impossibile da scassinare. Quando tutta questa storia di esplorazioni e bottini sarà finita, il bicchiere lo alzerai tu. Dal tuo castello con fossato. Furfante? E chi se ne frega. Hai vinto tu la partita!",

  // Manuale V1, r. 3679-3683
  suggestedNames: [
    // Umano
    'Veg',
    'Sparrow',
    'Shank',
    'Jack',
    'Marlow',
    'Dodge',
    'Rat',
    'Sprite',
    'Farley',
    'Hob',
    'Dora',
    // Elfo
    "Leon'thand",
    "Ar'meril",
    'Anarohir',
    'Nassha',
    'Rion',
    'Sarlal',
    'Dehtari',
    'Nerwe',
    'Lelal',
    'Fja',
    // Nano
    'Gasrin',
    'Nosdo',
    'Gilthe',
    'Thrilgeir',
    'Halin',
    'Tadral',
    'Inthud',
    'Thrilba',
    'Duskor',
    'Og',
    // Halfling
    'Felix',
    'Rook',
    'Mouse',
    'Sketch',
    'Trixie',
    'Robin',
    'Omar',
    'Brynn',
    'Bug',
    'Tod',
    'Rina',
    // Mezz'orco
    'Uga',
    'A',
    'Liktgrosh',
    'Daga',
    'Occhiopigro',
    'Jubag',
    'Uu',
    'Zock-ork',
    'Odra',
    'Tora',
  ],

  // Il Ladro non ha sezione "Titoli" nel manuale (è specifica del Barbaro)
  suggestedTitles: [],

  // Manuale V1, rr. 3685-3691
  aspetti: [
    {
      label: 'Occhi',
      options: ['Sfuggenti', 'Attenti', 'Furbi'],
    },
    {
      label: 'Capelli',
      options: ['Incappucciato', 'Spettinato', 'Capelli corti'],
    },
    {
      label: 'Vestiario',
      options: ['Abiti scuri', 'Abiti eleganti', 'Abiti comuni'],
    },
    {
      label: 'Corpo',
      options: ['Esile', 'Tozzo', 'Flaccido'],
    },
  ],

  // Manuale V1, rr. 3693-3699
  damageDie: 'd8',
  baseHp: 6,
  baseLoad: 9,

  // Manuale V1, rr. 3701-3709
  alignmentPrecepts: {
    buono: 'Ti fai avanti per aiutare gli indifesi e i perseguitati.',
    legale: 'Porti un criminale davanti alla giustizia.',
    neutrale: 'Eludi la sorveglianza o ti infiltri da qualche parte.',
    caotico: 'Ti tuffi nel pericolo senza un piano preciso.',
    malvagio: 'Scarichi i tuoi guai o le tue colpe su qualcun altro.',
  },

  // Manuale V1, rr. 3729-3735. maxBonds = 4 (deve coincidere con la lunghezza, FI-3)
  bondTemplates: [
    'Ho rubato qualcosa a _____.',
    '_____ mi copre le spalle quando le cose vanno male.',
    '_____ conosce dei dettagli incriminanti su di me.',
    'Io e _____ abbiamo in ballo un affare sporco.',
  ],
  maxBonds: 4,

  // Manuale V1, rr. 3737-3745
  archetypes: [
    {
      id: 'umano',
      name: 'Umano',
      associatedRace: 'umano',
      description:
        'Quando declami conoscenze o discerni la realtà riguardo ad attività criminali, prendi +1 al tiro.',
      grantedMoveId: 'ladro.archetipo-umano',
    },
    {
      id: 'elfo',
      name: 'Elfo',
      associatedRace: 'elfo',
      description:
        'Quando sfidi il pericolo lanciandoti in folli ed esagerate acrobazie, hai sempre +1 al tiro.',
      grantedMoveId: 'ladro.archetipo-elfo',
    },
    {
      id: 'nano',
      name: 'Nano',
      associatedRace: 'nano',
      description:
        'Quando discerni la realtà, aggiungi una domanda sulle cose preziose. Inoltre valuti a vista il valore di mercato di qualsiasi oggetto.',
      grantedMoveId: 'ladro.archetipo-nano',
    },
    {
      id: 'halfling',
      name: 'Halfling',
      associatedRace: 'halfling',
      description: "Quando attacchi con un'arma a distanza, infliggi +2 danni.",
      grantedMoveId: 'ladro.archetipo-halfling',
    },
    {
      id: 'mezzorco',
      name: "Mezz'orco",
      associatedRace: 'mezzorco',
      description: 'Quando infliggi danni con *Attacco Furtivo*, infliggi +1 danni.',
      grantedMoveId: 'ladro.archetipo-mezzorco',
    },
  ],

  // Mosse iniziali AUTOMATICHE: ID delle 4 mosse "starting" prese al lv 1 senza scelta
  startingMoves: ladroStartingAutoMoves.map((m) => m.id),

  // Mosse iniziali DI SCELTA: 1 gruppo, 1 di 3 (Avvelenatore / Speriamo che Tenga / Arte Persuasiva)
  startingChoiceGroups: [
    {
      id: 'mossa-iniziale-aggiuntiva',
      name: 'Mossa iniziale aggiuntiva',
      description: 'Scegli una di queste tre mosse oltre alle 4 iniziali automatiche.',
      moveOptions: ladroStartingChoiceMoves.map((m) => m.id),
      pickCount: 1,
      expandableInAdvanced: false,
    },
  ],

  advancedMoves2to5: ladroAdvanced2to5Moves.map((m) => m.id),
  advancedMoves6to10: ladroAdvanced6to10Moves.map((m) => m.id),

  // Il Ladro non è casteggiante: spellcasting resta undefined.

  // Manuale V1, rr. 3712-3727
  startingEquipment: {
    always: [
      {
        id: 'razioni-da-dungeon',
        name: 'Razioni da Dungeon',
        weight: 1,
        uses: 5,
        tags: ['razione'],
      },
      {
        id: 'armatura-di-cuoio-rigido',
        name: 'Armatura di cuoio rigido',
        weight: 1,
        armor: 1,
        tags: ['indossato'],
      },
      {
        id: 'monete',
        name: 'Monete',
        weight: 0,
        quantity: 10,
        tags: [],
        description: 'Soldi del PG.',
      },
    ],
    choices: [
      {
        id: 'arma-mischia-iniziale',
        prompt: 'Scegli le tue armi da mischia',
        pickCount: 1,
        options: [
          {
            id: 'pugnale-piu-spada-corta',
            label: 'Pugnale e Spada corta',
            items: [
              {
                id: 'pugnale',
                name: 'Pugnale',
                weight: 1,
                tags: ['corta'],
              },
              {
                id: 'spada-corta',
                name: 'Spada corta',
                weight: 1,
                tags: ['media'],
              },
            ],
          },
          {
            id: 'fioretto',
            label: 'Fioretto',
            items: [
              {
                id: 'fioretto',
                name: 'Fioretto',
                weight: 1,
                tags: ['media', 'precisa'],
              },
            ],
          },
        ],
      },
      {
        id: 'arma-distanza-iniziale',
        prompt: 'Scegli la tua arma a distanza',
        pickCount: 1,
        options: [
          {
            id: 'tre-pugnali-da-lancio',
            label: '3 Pugnali da lancio',
            items: [
              {
                id: 'pugnali-da-lancio',
                name: 'Pugnali da lancio',
                weight: 0,
                quantity: 3,
                tags: ['da lancio', 'vicino'],
              },
            ],
          },
          {
            id: 'arco-logoro-piu-faretra',
            label: 'Arco logoro e Faretra di frecce',
            items: [
              {
                id: 'arco-logoro',
                name: 'Arco logoro',
                weight: 2,
                tags: ['vicino', 'a due mani'],
              },
              {
                id: 'faretra-di-frecce',
                name: 'Faretra di frecce',
                weight: 1,
                uses: 3,
                tags: [],
                description: '3 munizioni.',
              },
            ],
          },
        ],
      },
      {
        id: 'extra-iniziale',
        prompt: 'Scegli un extra',
        pickCount: 1,
        options: [
          {
            id: 'pozione-curativa',
            label: 'Pozione Curativa',
            items: [
              {
                id: 'pozione-curativa',
                name: 'Pozione Curativa',
                weight: 0,
                uses: 1,
                tags: [],
                description: 'Recupera PF se bevuta.',
              },
            ],
          },
          {
            id: 'attrezzatura-da-avventuriero',
            label: 'Attrezzatura da Avventuriero',
            items: [
              {
                id: 'attrezzatura-da-avventuriero',
                name: 'Attrezzatura da Avventuriero',
                weight: 2,
                uses: 5,
                tags: [],
                description:
                  'Da questo mucchio informe di roba salterà sempre fuori qualcosa di utile.',
              },
            ],
          },
        ],
      },
    ],
  },
};
